import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, Globe, Lock, ExternalLink } from 'lucide-react';
import Button from './Button';
import toast from 'react-hot-toast';

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isPublic: boolean;
  onTogglePublic: () => void;
  noteId?: string;
}

const ShareDialog: React.FC<ShareDialogProps> = ({
  isOpen,
  onClose,
  isPublic,
  onTogglePublic,
  noteId
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!noteId) return;

    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/notes/${noteId}`
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Share link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.95 }}
          onClick={(e) => e.stopPropagation()}
          className="relative bg-background border rounded-lg shadow-lg max-w-md w-full p-6"
        >
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-4 top-4"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>

          <h2 className="text-lg font-semibold mb-4">Share Note</h2>

          <div className="space-y-6">
            <div className="p-4 rounded-lg bg-muted">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  {isPublic ? (
                    <Globe className="w-4 h-4 text-primary" />
                  ) : (
                    <Lock className="w-4 h-4 text-muted-foreground" />
                  )}
                  <span className="font-medium">
                    {isPublic ? 'Public' : 'Private'}
                  </span>
                </div>
                <Button
                  variant={isPublic ? 'destructive' : 'default'}
                  size="sm"
                  onClick={onTogglePublic}
                >
                  Make {isPublic ? 'Private' : 'Public'}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                {isPublic 
                  ? 'Anyone with the link can view this note'
                  : 'Only you can view this note'}
              </p>
            </div>

            {isPublic && noteId && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2"
              >
                <label className="block text-sm text-muted-foreground">
                  Share Link
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 p-2 bg-muted rounded-md font-mono text-sm truncate">
                    {window.location.origin}/notes/{noteId}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCopy}
                      className="min-w-[40px]"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-500" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => window.open(`/notes/${noteId}`, '_blank')}
                      className="min-w-[40px]"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ShareDialog;